import React from "react";
import { List } from "components";
import {
  StatusCell,
  AmountCell,
  ProjectCell,
  JobCell,
  DateCell,
  TypeCell,
  ExtendedNameCell,
} from "@/modules/work/billables/cells";
import { ROUTE, TODAY } from "@/lib/utils";
import OverdueBillableFilters from "@/modules/reports/billables/overdue/filters";
import { FIRST_DUE_DATE } from "@/lib/constants";
import { EmployeeCell } from "components/cell/employee/cell";
import { usePermissions } from "contexts/permission";
import { getEmployeeFields } from "components/user/config";

export default function Overdue() {
  const permissions = usePermissions();

  const canReadAmount = permissions.hasFieldAccess(
    "billables",
    "read",
    "amount",
  );

  const baseFields = [
    "id",
    "name",
    "type",
    "job.name",
    "job.project.name",
    "status",
    "billing_date",
    "due_date",
    "forecast_date",
    "extensions.start_date",
    "extensions.billing_date",
    "extensions.justification",
    "extensions.date_created",
    {
      job: [
        {
          project: ["client.company_id.name"],
        },
      ],
    },
    {
      job: [
        "id",
        getEmployeeFields("coordinator"),
        {
          project: [
            "id",
            "name",
            getEmployeeFields("coordinator"),
            getEmployeeFields("account_manager"),
            getEmployeeFields("sales_coordinator"),
            "project_id",
          ],
        },
      ],
    },
  ];

  const fields = [
    ...baseFields,
    ...(canReadAmount ? ["amount", "currency.name", "adjusted_amount"] : []),
  ];

  const columns = [
    {
      id: "name",
      header: "Billable",
      accessorKey: "name",
      cell: ({ row }) => (
        <ExtendedNameCell
          row={row}
          href={ROUTE.BILLABLE(row.original.id) + "/activity"}
        />
      ),
    },
    {
      id: "job.project.name",
      header: "Project",
      accessorKey: "job.project.name",
      cell: ProjectCell,
    },
    {
      id: "job",
      header: "Job",
      accessorKey: "job.name",
      cell: JobCell,
    },
    {
      id: "amount",
      header: "Amount",
      accessorKey: "amount",
      cell: AmountCell,
      field: "amount",
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "status",
      cell: StatusCell,
    },
    {
      id: "type",
      header: "Type",
      accessorKey: "type",
      cell: TypeCell,
    },
    {
      id: "billing_date",
      header: "Billing Date",
      accessorKey: "billing_date",
      sortable: true,
      cell: DateCell,
    },
    {
      id: "due_date",
      header: "Due Date",
      accessorKey: "due_date",
      sortable: true,
      cell: DateCell,
    },
    {
      id: "due_by",
      header: "Due by",
      accessorKey: "due_date",
      cell: ({ row }) => {
        const dueDate = new Date(row.original.due_date);
        const diff = new Date() - dueDate;
        return Math.floor(diff / (1000 * 60 * 60 * 24));
      },
    },
    {
      id: "forecast_date",
      header: "Forecast Date",
      accessorKey: "forecast_date",
      sortable: true,
      cell: DateCell,
    },
    {
      id: "job.coordinator",
      header: "JC",
      accessorKey: "job.coordinator",
      cell: EmployeeCell,
    },
    {
      id: "job.project.coordinator",
      header: "PC",
      accessorKey: "job.project.coordinator",
      cell: EmployeeCell,
    },
    {
      id: "job.project.account_manager",
      header: "AM",
      accessorKey: "job.project.account_manager",
      cell: EmployeeCell,
    },
    {
      id: "job.project.sales_coordinator",
      header: "SC",
      accessorKey: "job.project.sales_coordinator",
      cell: EmployeeCell,
    },
  ];

  const defaultFilters = {
    due_date: {
      _gte: FIRST_DUE_DATE,
      _lte: TODAY,
    },
    status: { _eq: "billed" },
  };

  return (
    <div className="flex-1 overflow-auto">
      <List
        title="Overdue"
        storageKey={`overdue-billables`}
        endPoint="billables"
        renderFilters={OverdueBillableFilters}
        filters={defaultFilters}
        columns={columns}
        meta={{ fields }}
        href={null}
        sortBy="forecast_date"
        sortOrder="asc"
      />
    </div>
  );
}
